import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { API_BASE_URL } from '../config/api';
import './AdminComponents.css';

const MobileConversationList = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();
  
  // User and conversation state
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI state
  const [includeDeleted, setIncludeDeleted] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('includeDeleted') === 'true';
  });

  useEffect(() => {
    loadUserAndConversations();
  }, [userId]);

  useEffect(() => {
    if (user) {
      loadUserConversations();
    }
  }, [includeDeleted]);

  const getAuthHeaders = () => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('auth_token='))
      ?.split('=')[1];
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const loadUserAndConversations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load user details
      const userResponse = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });

      if (!userResponse.ok) {
        throw new Error(`Failed to load user: ${userResponse.statusText}`);
      }

      const userData = await userResponse.json();
      setUser(userData.user);

      // Load user's conversations
      await loadUserConversations();

    } catch (err) {
      console.error('Error loading user and conversations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUserConversations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/conversations?limit=100&includeDeleted=${includeDeleted}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to load conversations: ${response.statusText}`);
      }

      const data = await response.json();
      setConversations(data.conversations || []);

    } catch (err) {
      console.error('Error loading conversations:', err);
      setError(err.message);
    }
  };

  const setShowDeleted = (showDeleted) => {
    setIncludeDeleted(showDeleted);
    
    // Update URL to reflect the change
    const url = new URL(window.location);
    if (showDeleted) {
      url.searchParams.set('includeDeleted', 'true');
    } else {
      url.searchParams.delete('includeDeleted');
    }
    window.history.replaceState({}, '', url);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const handleConversationClick = (conversationId) => {
    navigate(`/admin/users/${userId}/conversation/${conversationId}${includeDeleted ? '?includeDeleted=true' : ''}`);
  };

  // Authentication checks
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="mobile-conversation-list">
        <div className="mobile-header">
          <Link to={`/admin/users/${userId}`} className="back-button">
            ← Back
          </Link>
          <h2>Conversations</h2>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading conversations...</p>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="mobile-conversation-list">
        <div className="mobile-header">
          <Link to={`/admin/users/${userId}`} className="back-button">
            ← Back
          </Link>
          <h2>Error</h2>
        </div>
        <div className="error-state">
          <h3>⚠️ Error Loading Conversations</h3>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={loadUserAndConversations} className="retry-button">
              🔄 Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-conversation-list">
      {/* Mobile Header */}
      <div className="mobile-header">
        <Link to={`/admin/users/${userId}`} className="back-button">
          ← Back
        </Link>
        <div className="header-info">
          <h2>Conversations</h2>
          <p className="user-info">{user?.email}</p>
        </div>
      </div>



      {/* Content wrapper */}
      <div className="mobile-content-wrapper">
        {error && (
          <div className="error-message">
            <span>⚠️ {error}</span>
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {/* Controls */}
        <div className="mobile-controls">
          <div className="conversation-count">
            {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
          </div>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
            <span className="checkbox-text">Show deleted</span>
          </label>
        </div>

        {/* Conversations List */}
        <div className="mobile-conversations-container">
        {conversations.length === 0 ? (
          <div className="empty-conversations">
            <p>No conversations found</p>
            {includeDeleted && <p>Try unchecking "Show deleted" to see active conversations.</p>}
          </div>
        ) : (
          <div className="mobile-conversations-list">
            {conversations
              .sort((a, b) => new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt))
              .map(conversation => (
                <div
                  key={conversation.conversationId}
                  className={`mobile-conversation-item ${conversation.deleted ? 'deleted' : ''}`}
                  onClick={() => handleConversationClick(conversation.conversationId)}
                >
                  <div className="conversation-main">
                    <div className="conversation-title">
                      <h3>{conversation.title || 'Untitled Conversation'}</h3>
                      {conversation.deleted && (
                        <span className="deleted-badge">🗑️ DELETED</span>
                      )}
                    </div>
                    <div className="conversation-arrow">›</div>
                  </div>
                  
                  <div className="conversation-details">
                    <div className="conversation-meta">
                      <span className="conversation-date">
                        {formatTimestamp(conversation.lastMessageAt || conversation.createdAt)}
                      </span>
                      {conversation.messageCount && (
                        <span className="message-count">
                          {conversation.messageCount} messages
                        </span>
                      )}
                      {conversation.model && (
                        <span className="model-info">
                          {conversation.model}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default MobileConversationList;